import apiClient from './apiClient';

export interface OrganizationUser {
	id: string;
	firstName: string;
	lastName: string;
	email: string;
	role: string;
	roleHR: boolean;
	rolePM: boolean;
	roleLegal: boolean;
	roleFinance: boolean;
	isLegalRepresentative: boolean;
	status: string;
}

export interface Organization {
	id: string;
	name: string;
	legalName: string;
	vatNumber: string;
	fiscalCode: string;
	italianSdiCode: string;
	billingAddress: string;
	billingCity: string;
	billingProvince: string;
	billingZipCode: string;
	billingCountryCode: string;
	billingEmail: string;
	certifiedEmail: string;
	logoUrl: string | null;
	users: OrganizationUser[];
}

export interface CreateOrganizationRequest {
	name: string;
	legalName: string;
	vatNumber: string;
	fiscalCode: string;
	italianSdiCode: string;
	billingAddress: string;
	billingCity: string;
	billingProvince: string;
	billingZipCode: string;
	billingCountryCode: string;
	billingEmail: string;
	certifiedEmail: string;
}

export interface UpdateOrganizationRequest {
	name: string;
	legalName: string;
	vatNumber: string;
	fiscalCode: string;
	italianSdiCode: string;
	billingAddress: string;
	billingCity: string;
	billingProvince: string;
	billingZipCode: string;
	billingCountryCode: string;
	billingEmail: string;
	certifiedEmail: string;
}

export interface InviteCollaboratorRequest {
	fullName: string;
	email: string;
	roleHR: boolean;
	rolePM: boolean;
	roleLegal: boolean;
	roleFinance: boolean;
	isLegalRepresentative: boolean;
}

export interface PendingInvitation {
	id: string;
	fullName: string;
	email: string;
	createdAt: string;
}

const getOrganizations = async (): Promise<Organization[]> => {
	try {
		const response = await apiClient.get('/api/organizations');
		return response.data;
	} catch (error) {
		console.error('Error fetching organizations:', error);
		throw error;
	}
};

const getOrganizationById = async (id: string): Promise<Organization> => {
	try {
		const response = await apiClient.get(`/api/organizations/${id}`);
		return response.data;
	} catch (error) {
		console.error(`Error fetching organization with ID ${id}:`, error);
		throw error;
	}
};

const createOrganization = async (data: CreateOrganizationRequest): Promise<Organization> => {
	try {
		const response = await apiClient.post('/api/organizations', data);
		return response.data;
	} catch (error) {
		console.error('Error creating organization:', error);
		throw error;
	}
};

const updateOrganization = async (
	id: string,
	data: UpdateOrganizationRequest,
): Promise<Organization> => {
	try {
		const response = await apiClient.put(`/api/organizations/${id}`, data);
		return response.data;
	} catch (error) {
		console.error(`Error updating organization with ID ${id}:`, error);
		throw error;
	}
};

const inviteCollaborator = async (
	orgId: string,
	data: InviteCollaboratorRequest,
): Promise<void> => {
	await apiClient.post(`/api/organizations/${orgId}/collaborators/invite`, data);
};

const getPendingInvitations = async (orgId: string): Promise<PendingInvitation[]> => {
	const response = await apiClient.get(`/api/organizations/${orgId}/invitations/pending`);
	return response.data;
};

const removeCollaborator = async (orgId: string, userId: string): Promise<void> => {
	await apiClient.delete(`/api/organizations/${orgId}/collaborators/${userId}`);
};

export const organizationService = {
	getOrganizations,
	getOrganizationById,
	createOrganization,
	updateOrganization,
	inviteCollaborator,
	removeCollaborator,
	getPendingInvitations,
};

export default organizationService;
